'use client';

import { Tooltip } from '../atomic/Tooltip';
import { Briefcase, Car, Home, Leaf } from 'lucide-react';

// Average values for calculations
const AVERAGE_ANNUAL_SALARY = 45000; // Average annual salary in euros
const AVERAGE_ELECTRIC_CAR_COST = 40000; // Average cost of an electric car in euros
const AVERAGE_HOUSEHOLD_CONSUMPTION = 3500; // Average annual household electricity consumption in kWh
// const AVERAGE_SOLAR_PANEL_OUTPUT = 400; // Average annual output per solar panel in kWh
const CO2_SAVINGS_PER_MWH = 0.4; // Tons of CO2 saved per MWh of renewable energy

// Impact item type
type ImpactItem = {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  iconBg: string;
  iconColor: string;
  title: string;
  description: string;
  value: number;
  valueFormatter: (value: number) => string;
};

interface ImpactCardProps {
  /**
   * The potential annual savings in euros
   */
  annualSavings: number;
  
  /**
   * The total annual energy production in kWh
   */
  annualProductionKwh: number;
  
  /**
   * Additional CSS classes
   */
  className?: string;
  
  /**
   * Whether to show a more compact version
   */
  compact?: boolean;
}

/**
 * [DC01] Molecular component that shows the potential impact of energy savings
 * in real-world terms like jobs created, equivalent electric cars, etc.
 */
export function ImpactCard({ 
  annualSavings, 
  annualProductionKwh,
  className = '',
  compact = false 
}: ImpactCardProps) {
  // Format large numbers with thousands separators
  const formatNumber = (num: number) => num.toLocaleString('de-AT');
  
  // Format currency
  const formatCurrency = (amount: number) => 
    new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
      .format(amount)
      .replace(/\u00A0/g, ' '); // Replace non-breaking spaces with normal spaces

  // Calculate various impact metrics
  const impactItems: ImpactItem[] = [
    {
      id: 'jobs',
      icon: Briefcase,
      iconBg: 'bg-green-100 dark:bg-green-900/30',
      iconColor: 'text-green-600 dark:text-green-400',
      title: 'Arbeitsplätze',
      description: `könnten mit ${formatCurrency(annualSavings)} pro Jahr finanziert werden`,
      value: Math.round(annualSavings / AVERAGE_ANNUAL_SALARY),
      valueFormatter: (value) => formatNumber(value)
    },
    {
      id: 'cars',
      icon: Car,
      iconBg: 'bg-blue-100 dark:bg-blue-900/30',
      iconColor: 'text-blue-600 dark:text-blue-400',
      title: 'E-Autos',
      description: 'könnten mit den jährlichen Einsparungen gekauft werden',
      value: Math.round(annualSavings / AVERAGE_ELECTRIC_CAR_COST),
      valueFormatter: (value) => formatNumber(value)
    },
    {
      id: 'households',
      icon: Home,
      iconBg: 'bg-yellow-100 dark:bg-yellow-900/30',
      iconColor: 'text-yellow-600 dark:text-yellow-400',
      title: 'Haushalte',
      description: 'könnten mit der erzeugten Energie versorgt werden',
      value: Math.round(annualProductionKwh / AVERAGE_HOUSEHOLD_CONSUMPTION),
      valueFormatter: (value) => formatNumber(value)
    },
    {
      id: 'co2',
      icon: Leaf,
      iconBg: 'bg-purple-100 dark:bg-purple-900/30',
      iconColor: 'text-purple-600 dark:text-purple-400',
      title: 'CO₂ Einsparung',
      description: 'könnten jährlich eingespart werden',
      value: Math.round((annualProductionKwh / 1000) * CO2_SAVINGS_PER_MWH),
      valueFormatter: (value) => `${formatNumber(value)} Tonnen`
    }
  ];
  
  // Filter out items with zero or negative values
  const validImpactItems = impactItems.filter(item => item.value > 0);

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden ${className}`}>
      <div className="p-4 sm:p-5 border-b border-gray-100 dark:border-gray-700">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center">
          <span>Mögliche Auswirkungen</span>
          <Tooltip 
            content="Diese Zahlen zeigen, was mit den potenziellen jährlichen Einsparungen möglich wäre."
            className="ml-1"
            iconClassName="text-gray-400 dark:text-gray-500 w-4 h-4 inline-block align-text-bottom"
          />
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Was bedeuten {formatCurrency(annualSavings)} jährlich?
        </p>
      </div>
      
      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {validImpactItems.map((item) => (
          <div key={item.id} className="p-4 sm:p-5 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
            <div className="flex items-start">
              <div className={`flex-shrink-0 h-10 w-10 rounded-lg ${item.iconBg} flex items-center justify-center`}>
                <item.icon className={`${item.iconColor} w-5 h-5`} />
              </div>
              <div className="ml-4 flex-1 min-w-0">
                <div className="flex items-baseline">
                  <p className="text-lg font-semibold text-gray-900 dark:text-white mr-2">
                    {item.valueFormatter(item.value)}
                  </p>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    {item.title}
                  </p>
                </div>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {item.description}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {!compact && validImpactItems.length > 0 && (
        <div className="px-4 sm:px-5 py-3 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
            * Basierend auf Durchschnittswerten. Die tatsächlichen Auswirkungen können variieren.
          </p>
        </div>
      )}
      
      {validImpactItems.length === 0 && (
        <div className="p-8 text-center">
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            Keine ausreichenden Daten verfügbar, um die Auswirkungen zu berechnen.
          </p>
        </div>
      )}
    </div>
  );
}
